<template>
  <section class="dash-panel">
    <header class="dash-panel-head">
      <div>
        <span class="dash-eyebrow">Suivi</span>
        <h3 class="dash-panel-title">Colis par statut</h3>
        <p class="dash-panel-sub">Répartition des colis selon les filtres</p>
      </div>
      <button
        class="btn btn-sm btn-outline-secondary pc-filter-btn"
        type="button"
        data-bs-toggle="modal"
        data-bs-target="#filterLeadModal"
      >
        <i class="ph ph-funnel" aria-hidden="true"></i>
        Filtres
        <span v-if="activeFilters" class="pc-filter-count">{{ activeFilters }}</span>
      </button>
    </header>

    <AppLoader v-if="isLoader && !legend.length" label="Chargement des statuts…" />
    <div v-else-if="!total" class="dash-empty">
      <i class="ph ph-chart-donut" aria-hidden="true"></i>
      <p>Aucun colis pour ces filtres.</p>
    </div>
    <div v-else class="pc-body" :class="{ 'is-refreshing': isLoader }">
      <div class="pc-chart">
        <apexchart type="donut" height="250" :options="donutOptions" :series="seriesNumbers" />
      </div>
      <ul class="pc-legend">
        <li v-for="item in legend" :key="item.label" class="pc-item">
          <span class="pc-dot" :style="{ background: item.color }"></span>
          <span class="pc-label">{{ item.label }}</span>
          <span class="pc-count">{{ item.value.toLocaleString("fr-FR") }}</span>
          <span class="pc-share">{{ item.share }}%</span>
        </li>
      </ul>
    </div>
  </section>

  <!-- Advanced filters -->
  <div class="modal fade" id="filterLeadModal" tabindex="-1" aria-labelledby="filterLeadModalTitle" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title" id="filterLeadModalTitle">
            <i class="ph ph-funnel" aria-hidden="true"></i> Filtrer les colis
          </h2>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
        </div>
        <div class="modal-body">
          <form class="row g-3" @submit.prevent>
            <div class="col-md-4">
              <label class="form-label" for="pc-start">Date de</label>
              <input id="pc-start" type="date" class="form-control" v-model="searchSDate" />
            </div>
            <div class="col-md-4">
              <label class="form-label" for="pc-end">Date fin</label>
              <input id="pc-end" type="date" class="form-control" v-model="searchEDate" />
            </div>
            <div class="col-md-4">
              <label class="form-label" for="pc-type">Type colis</label>
              <select id="pc-type" class="form-select" v-model="type">
                <option value="" selected>Tous</option>
                <option value="SIMPLE">Colis Simples</option>
                <option value="STOCK">Colis Stock</option>
                <option value="SUPPLIER">Colis DropShippers</option>
                <option value="SIMPLE_STOCK">Colis Produits</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Magasin</label>
              <v-select
                label="name"
                :options="ClientList"
                v-model="customer"
                placeholder="Chercher un client"
                @search="onSearchClients"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">Zone</label>
              <v-select
                label="name"
                :options="ZonesList"
                v-model="zone"
                placeholder="Chercher une zone"
                @search="onSearchZones"
                @option:selected="FetchAgencyList"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">Agence</label>
              <v-select
                label="name"
                :options="AgencyList"
                v-model="agency_receiving"
                placeholder="Chercher une agence"
                @option:selected="FetchCitiesList"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">Ville</label>
              <v-select
                label="name"
                :options="CitiesList"
                v-model="city"
                placeholder="Chercher une ville"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">Livreur</label>
              <v-select
                label="name"
                :options="LivreurList"
                v-model="livreur"
                placeholder="Chercher un livreur"
                @search="onSearchDrivers"
              />
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" @click="ResetAll" class="btn btn-outline-secondary">
            Réinitialiser
          </button>
          <button type="button" @click="FilterData" class="btn btn-primary" data-bs-dismiss="modal">
            Appliquer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import api from "@/utils/axios";
import { dashboardGet } from "@/utils/dashboardApi";
import { computed, defineComponent, onMounted, ref, watch } from "vue";

export default defineComponent({
  name: "ParcelCharts",
  // Period from the dashboard header (the modal can still refine it)
  props: {
    periodStart: { type: String, default: "" },
    periodEnd: { type: String, default: "" },
    periodVersion: { type: Number, default: 0 },
  },
  setup(props) {
    const chartData = ref<any>({});

    const searchSDate = ref("");
    const searchEDate = ref("");
    const exist = ref(false);
    const city = ref({ id: 0, name: "" });
    const livreur = ref({ id: 0, name: "" });
    const customer = ref({ id: 0, name: "" });
    const type = ref("");
    const zone = ref({ id: "", name: "" });
    const agency_receiving = ref({ id: 0, name: "" });
    const ClientList = ref<any[]>([]);
    const LivreurList = ref<any[]>([]);
    const CitiesList = ref<any[]>([]);
    const ZonesList = ref<any[]>([]);
    const AgencyList = ref<any[]>([]);
    const show = ref(false);

    const customersPie = ref({
      series: [],
      chartOptions: {
        chart: {
          toolbar: {
            show: false,
          },
          animations: {
            enabled: true,
          },
        },
        labels: [
          "Nouveaux Coliss",
          "Attente De Ramassage",
          "Ramassés",
          "Expédiés",
          "Reçus",
          "Mise en distribution",
          "En cours",
          "Retournés",
          "Livrés",
        ],
        dataLabels: {
          enabled: true,
        },
        legend: { show: false },
        stroke: {
          width: 4,
        },
        colors: ["#084C9D"],
      },
      noData: {
        text: "There's no data",
        align: "center",
        verticalAlign: "middle",
        offsetX: 0,
        offsetY: 0,
      },
    });

    const isLoader = ref(false);

    // ── Display helpers (donut + legend) ──
    const seriesNumbers = computed<number[]>(() =>
      ((customersPie.value.series as any[]) || []).map((v) => Number(v) || 0)
    );
    const total = computed(() => seriesNumbers.value.reduce((a, b) => a + b, 0));
    const legend = computed(() => {
      const labels: string[] = (customersPie.value.chartOptions as any).labels || [];
      const colors: string[] = (customersPie.value.chartOptions as any).colors || [];
      return seriesNumbers.value
        .map((value, i) => ({
          label: labels[i] || "—",
          color: colors[i] || "#A8A29E",
          value,
          share: total.value ? Math.round((value / total.value) * 1000) / 10 : 0,
        }))
        .filter((item) => item.value > 0)
        .sort((a, b) => b.value - a.value);
    });
    const donutOptions = computed(() => ({
      chart: { type: "donut", fontFamily: "Inter, sans-serif", toolbar: { show: false } },
      labels: (customersPie.value.chartOptions as any).labels || [],
      colors: (customersPie.value.chartOptions as any).colors || [],
      legend: { show: false },
      dataLabels: { enabled: false },
      // SVG attributes can't use var(): resolve the token (light/dark) now
      stroke: {
        width: 3,
        colors: [getComputedStyle(document.body).getPropertyValue("--ui-surface").trim() || "#FFFFFF"],
      },
      tooltip: { y: { formatter: (val: number) => `${val.toLocaleString("fr-FR")} colis` } },
      plotOptions: {
        pie: {
          donut: {
            size: "72%",
            labels: {
              show: true,
              name: { fontSize: "12px", fontWeight: 700, color: "#78716C", offsetY: 18 },
              value: {
                fontSize: "26px",
                fontWeight: 900,
                color: "#1C1917",
                offsetY: -14,
                formatter: (val: string) => Number(val).toLocaleString("fr-FR"),
              },
              total: {
                show: true,
                label: "Total colis",
                fontSize: "12px",
                fontWeight: 700,
                color: "#78716C",
                formatter: () => total.value.toLocaleString("fr-FR"),
              },
            },
          },
        },
      },
    }));
    // Number of advanced filters in use (badge on the "Filtres" button)
    const activeFilters = computed(
      () =>
        [type.value, customer.value?.id, zone.value?.id, agency_receiving.value?.id, city.value?.id, livreur.value?.id].filter(
          (v) => v !== "" && v !== 0 && v !== null && v !== undefined
        ).length
    );

    // only the answer of the latest filters is shown
    let lastRequest = 0;

    async function getData() {
      const request = ++lastRequest;
      isLoader.value = true;
      try {
        const data = await dashboardGet(
          `/api/auth/ParcelsData?searchSDate=${searchSDate.value}&searchEDate=${
            searchEDate.value
          }&city_id=${city.value?.id ?? ""}&customer=${
            customer.value?.id ?? ""
          }&types=${type.value ?? ""}&zone=${
            zone.value?.id ?? ""
          }&agency_receiving=${agency_receiving.value?.id ?? ""}&livreur=${
            livreur.value?.id ?? ""
          }`
        );
        if (request !== lastRequest) return;
        chartData.value = data.list;
        customersPie.value.series = data.series;
        customersPie.value.chartOptions = {
          ...customersPie.value.chartOptions,
          colors: data.colors,
          labels: data.lables,
        };
      } catch (error) {
        // handle error (e.g. show notification)
      } finally {
        if (request === lastRequest) isLoader.value = false;
      }
    }

    function isAllZero(arr: number[]): boolean {
      return arr.every((item) => item === 0);
    }

    // Filter data and show sidebar
    function FilterData() {
      getData();
      // If you use a sidebar component, trigger its hide method here
      // Example: sidebar2.value?.hide();
      show.value = true;
    }

    // Reset all filters and reload data
    function ResetAll() {
      searchSDate.value = "";
      searchEDate.value = "";
      type.value = "";
      city.value = { id: 0, name: "" };
      customer.value = { id: 0, name: "" };
      agency_receiving.value = { id: 0, name: "" };
      livreur.value = { id: 0, name: "" };
      zone.value = { id: "", name: "" };
      getData();
      // Example: sidebar2.value?.hide();
    }

    // Download Excel file
    async function GetExcel() {
      try {
        const res = await api.get(
          `/api/auth/ExcelTopData?type=Parcels&searchSDate=${searchSDate.value}&searchEDate=${searchEDate.value}&city_id=${city.value.id}&customer=${customer.value.id}&types=${type.value}&zone=${zone.value.id}&agency_receiving=${agency_receiving.value.id}&livreur=${livreur.value.id}`,
          { responseType: "arraybuffer" }
        );
        const fileURL = window.URL.createObjectURL(
          new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.download = "StatistiquesColis.xlsx";
        fileLink.click();
      } catch (error) {
        // handle error (e.g. show notification)
      }
    }

    // Fetch agency list by zone
    async function FetchAgencyList() {
      if (!zone.value?.id) return;
      try {
        const res = await api.get(`/api/auth/AgenceisZone/${zone.value.id}`);
        AgencyList.value = res.data;
      } catch (error) {
        // handle error
      }
    }

    // Fetch cities list by agency
    async function FetchCitiesList() {
      if (!agency_receiving.value?.id) return;
      try {
        const res = await api.get(
          `/api/auth/CitiesAgency/${agency_receiving.value.id}`
        );
        CitiesList.value = res.data;
      } catch (error) {
        // handle error
      }
    }

    // Search clients for dropdown/autocomplete
    async function onSearchClients(
      search: string,
      loading: (state: boolean) => void
    ) {
      if (search.length && ClientList.value.length === 0) {
        loading(true);
        try {
          const response = await api.get(`/api/auth/ListCustomer`);
          ClientList.value = response.data;
        } catch (error) {
          // handle error
        } finally {
          loading(false);
        }
      }
    }

    // Search zones for dropdown/autocomplete
    async function onSearchZones(
      search: string,
      loading: (state: boolean) => void
    ) {
      if (search.length && ZonesList.value.length === 0) {
        loading(true);
        try {
          const response = await api.get(`/api/auth/ListZone`);
          ZonesList.value = response.data;
        } catch (error) {
          // handle error
        } finally {
          loading(false);
        }
      }
    }

    // Search drivers for dropdown/autocomplete
    async function onSearchDrivers(
      search: string,
      loading: (state: boolean) => void
    ) {
      if (search.length && LivreurList.value.length === 0) {
        loading(true);
        try {
          const response = await api.get(`/api/auth/LivsList`);
          LivreurList.value = response.data;
        } catch (error) {
          // handle error
        } finally {
          loading(false);
        }
      }
    }

    watch(
      () => props.periodVersion,
      () => {
        searchSDate.value = props.periodStart;
        searchEDate.value = props.periodEnd;
        getData();
      }
    );

    onMounted(async () => {
      searchSDate.value = props.periodStart;
      searchEDate.value = props.periodEnd;
      getData();
    });
    return {
      customersPie,
      chartData,
      getData,
      isAllZero,
      searchSDate,
      searchEDate,
      exist,
      city,
      livreur,
      customer,
      type,
      zone,
      agency_receiving,
      ClientList,
      LivreurList,
      CitiesList,
      ZonesList,
      AgencyList,
      show,
      FilterData,
      ResetAll,
      isLoader,
      seriesNumbers,
      total,
      legend,
      donutOptions,
      activeFilters,
      onSearchClients,
      onSearchZones,
      onSearchDrivers,
      FetchAgencyList,
      FetchCitiesList,
      GetExcel,
    };
  },
});
</script>

<style scoped>
.pc-filter-btn {
  flex: 0 0 auto;
  gap: 6px;
}
.pc-filter-count {
  display: grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-accent);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: var(--ui-fw-black);
}
.pc-body {
  display: grid;
  grid-template-columns: minmax(0, 230px) minmax(0, 1fr);
  align-items: center;
  gap: var(--ui-space-5);
  transition: opacity var(--ui-dur) var(--ui-ease);
}
.pc-body.is-refreshing {
  opacity: 0.5;
  pointer-events: none;
}
.pc-legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.pc-item {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 46px;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: var(--ui-radius-sm);
  transition: background-color var(--ui-dur-fast) var(--ui-ease);
}
.pc-item:hover {
  background: var(--ui-surface-sunken);
}
.pc-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.pc-label {
  overflow: hidden;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pc-count {
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
  font-variant-numeric: tabular-nums;
}
.pc-share {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
}
:global(.dark) .pc-chart :deep(.apexcharts-datalabel-value) {
  fill: #F5F5F4 !important;
}
@media (max-width: 767.98px) {
  .pc-body {
    grid-template-columns: 1fr;
  }
  .pc-chart {
    max-width: 250px;
    margin: 0 auto;
  }
}
</style>
